import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { hrApi } from '@/api';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import type { HrOrgStructure } from '@/types/hr';
import { showApiError } from '@/utils/apiError';

type OrgSelectProps = {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
};

let orgStructureCache: HrOrgStructure | null = null;
let orgStructurePromise: Promise<HrOrgStructure> | null = null;
const orgStructureListeners = new Set<() => void>();

export function invalidateOrgStructureCache() {
  orgStructureCache = null;
  orgStructurePromise = null;
  orgStructureListeners.forEach((fn) => fn());
}

export function refreshOrgStructureCache(data: HrOrgStructure) {
  orgStructureCache = data;
  orgStructurePromise = Promise.resolve(data);
  orgStructureListeners.forEach((fn) => fn());
}

export function fetchOrgStructure() {
  if (orgStructureCache) return Promise.resolve(orgStructureCache);
  if (!orgStructurePromise) {
    orgStructurePromise = hrApi.getOrgStructure().then((data) => {
      orgStructureCache = data;
      return data;
    });
  }
  return orgStructurePromise;
}

function useHrOrgStructure(enabled: boolean) {
  const [structure, setStructure] = useState<HrOrgStructure | null>(orgStructureCache);
  const [loading, setLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    const onRefresh = () => setRefreshToken((t) => t + 1);
    orgStructureListeners.add(onRefresh);
    return () => {
      orgStructureListeners.delete(onRefresh);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (orgStructureCache) {
      setStructure(orgStructureCache);
      return;
    }
    setLoading(true);
    fetchOrgStructure()
      .then(setStructure)
      .catch((err) => showApiError(err, 'Failed to load org structure'))
      .finally(() => setLoading(false));
  }, [enabled, refreshToken]);

  return { structure, loading };
}

function emptyHint(tab: string, label: string) {
  return (
    <p className="mt-1 text-xs text-slate-500">
      No {label} yet.{' '}
      <Link to={`/hr/master-data?tab=${tab}`} className="text-primary-600 hover:underline">
        Set up in HR master data
      </Link>
    </p>
  );
}

export function DepartmentSelect({
  label = 'Department',
  value = '',
  onChange,
  placeholder = 'Select department',
  className,
  required,
  disabled,
  enabled = true,
}: OrgSelectProps & { enabled?: boolean }) {
  const { structure, loading } = useHrOrgStructure(enabled);
  const options = useMemo(
    () =>
      (structure?.departments ?? [])
        .filter((d) => d.active)
        .map((d) => ({ value: String(d.id), label: `${d.name} (${d.code})` })),
    [structure?.departments],
  );

  return (
    <>
      <SearchableSelect
        className={className}
        label={label}
        required={required}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        loading={loading}
        searchPlaceholder="Search departments…"
        emptyMessage="No departments found"
      />
      {!loading && options.length === 0 && emptyHint('departments', 'departments')}
    </>
  );
}

export function DesignationSelect({
  label = 'Designation',
  value = '',
  onChange,
  placeholder = 'Select designation',
  className,
  required,
  disabled,
  enabled = true,
}: OrgSelectProps & { enabled?: boolean }) {
  const { structure, loading } = useHrOrgStructure(enabled);
  const options = useMemo(
    () =>
      (structure?.designations ?? [])
        .filter((d) => d.active)
        .map((d) => {
          const gradeLabel = structure?.grades?.find((g) => g.id === d.gradeId)?.code ?? d.grade;
          return {
            value: String(d.id),
            label: gradeLabel ? `${d.name} (${d.code}) · ${gradeLabel}` : `${d.name} (${d.code})`,
          };
        }),
    [structure?.designations, structure?.grades],
  );

  return (
    <>
      <SearchableSelect
        className={className}
        label={label}
        required={required}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        loading={loading}
        searchPlaceholder="Search designations…"
        emptyMessage="No designations found"
      />
      {!loading && options.length === 0 && emptyHint('designations', 'designations')}
    </>
  );
}

export function GradeSelect({
  label = 'Grade',
  value = '',
  onChange,
  placeholder = 'Select grade',
  className,
  required,
  disabled,
  enabled = true,
}: OrgSelectProps & { enabled?: boolean }) {
  const { structure, loading } = useHrOrgStructure(enabled);
  const options = useMemo(
    () =>
      (structure?.grades ?? [])
        .filter((g) => g.active)
        .sort((a, b) => a.levelOrder - b.levelOrder)
        .map((g) => ({ value: String(g.id), label: `${g.name} (${g.code}) · L${g.levelOrder}` })),
    [structure?.grades],
  );

  return (
    <>
      <SearchableSelect
        className={className}
        label={label}
        required={required}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        loading={loading}
        searchPlaceholder="Search grades…"
        emptyMessage="No grades found"
      />
      {!loading && options.length === 0 && emptyHint('grades', 'grades')}
    </>
  );
}

export function BusinessUnitSelect({
  label = 'Business unit',
  value = '',
  onChange,
  placeholder = 'Select business unit',
  className,
  required,
  disabled,
  enabled = true,
}: OrgSelectProps & { enabled?: boolean }) {
  const { structure, loading } = useHrOrgStructure(enabled);
  const options = useMemo(
    () =>
      (structure?.businessUnits ?? [])
        .filter((b) => b.active)
        .map((b) => ({ value: String(b.id), label: `${b.name} (${b.code})` })),
    [structure?.businessUnits],
  );

  return (
    <>
      <SearchableSelect
        className={className}
        label={label}
        required={required}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        loading={loading}
        searchPlaceholder="Search business units…"
        emptyMessage="No business units found"
      />
      {!loading && options.length === 0 && emptyHint('businessUnits', 'business units')}
    </>
  );
}
